import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Users, Layers, Gift, Hash, MessageSquare, TrendingUp,
  CheckCircle, Circle, ArrowRight, Radio, Globe, Lock,
  Download, Loader2, AlertCircle, Database
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const StatCard = ({ title, value, icon: Icon, color, testId }) => (
  <Card className="bg-[#121212] border-[#27272A] hover:border-[#3f3f46] transition-colors duration-150">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-[#A1A1AA] uppercase tracking-wider font-medium">{title}</p>
          <p className="text-2xl font-bold text-white mt-1 font-heading" data-testid={testId}>{typeof value === 'number' ? value.toLocaleString("tr-TR") : value}</p>
        </div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

function ImportProgressBanner({ progress, onDismiss }) {
  if (!progress || !progress.running) return null;

  const isCompleted = progress.status === "completed";
  const isError = progress.status?.startsWith("error");
  const pct = progress.channels_total > 0
    ? Math.round((progress.channels_done / progress.channels_total) * 100)
    : 0;

  return (
    <Card className={`mb-6 border ${isCompleted ? "border-[#34C759]/30 bg-[#34C759]/5" : isError ? "border-[#FF3B30]/30 bg-[#FF3B30]/5" : "border-[#24A1DE]/30 bg-[#24A1DE]/5"}`} data-testid="import-progress-banner">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-2">
          {isCompleted ? (
            <CheckCircle className="w-5 h-5 text-[#34C759] shrink-0" />
          ) : isError ? (
            <AlertCircle className="w-5 h-5 text-[#FF3B30] shrink-0" />
          ) : (
            <Loader2 className="w-5 h-5 text-[#24A1DE] animate-spin shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white">
              {isCompleted ? "Ice aktarma tamamlandi!" : isError ? "Hata olustu" : "Kullanicilar ice aktariliyor..."}
            </p>
            <p className="text-xs text-[#A1A1AA] mt-0.5 truncate">
              {isCompleted ? progress.message : isError ? progress.status?.replace("error: ", "") : progress.status || "Baslatiliyor..."}
            </p>
          </div>
          {isCompleted && (
            <Button variant="ghost" size="sm" className="text-[#A1A1AA] hover:text-white h-7 px-2" onClick={onDismiss}>Kapat</Button>
          )}
        </div>
        {!isCompleted && !isError && (
          <>
            <Progress value={pct} className="h-1.5 bg-[#27272A] mb-2" />
            <div className="flex items-center justify-between text-xs text-[#A1A1AA]">
              <span>{progress.current_channel || "..."}</span>
              <div className="flex gap-3">
                <span>{progress.channels_done || 0}/{progress.channels_total || 0} kanal</span>
                <span className="text-[#34C759]">{(progress.imported || 0).toLocaleString("tr-TR")} yeni</span>
                <span>{(progress.total_found || 0).toLocaleString("tr-TR")} bulundu</span>
                <span>{progress.sessions_used || 0} oturum</span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importProgress, setImportProgress] = useState(null);
  const [importing, setImporting] = useState(false);
  const pollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { loadData(); checkImportStatus(); return () => clearInterval(pollRef.current); }, []);

  const loadData = async () => {
    try {
      const [statsRes, setupRes] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/settings/setup-status")
      ]);
      setStats(statsRes.data);
      setSetup(setupRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const checkImportStatus = async () => {
    try {
      const { data } = await api.get("/users/import-status");
      if (data.running) {
        setImportProgress(data);
        setImporting(true);
        startPolling();
      }
    } catch {}
  };

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/users/import-status");
        setImportProgress(data);
        if (!data.running) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setImporting(false);
          loadData(); // Refresh stats
          if (data.status === "completed") toast.success("Tum kanallardan ice aktarma tamamlandi!");
        }
      } catch { }
    }, 3000);
  }, []);

  const startImportAll = async () => {
    try {
      setImporting(true);
      const { data } = await api.post("/users/import-all-members");
      if (data.status === "running") {
        setImportProgress(data.progress);
      }
      toast.info("Tum kanallardan ice aktarma baslatildi");
      startPolling();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ice aktarma basarilamadi");
      setImporting(false);
    }
  };

  const stepRoutes = { bot: "/settings", channel: "/settings", import: "/users", groups: "/groups", channels: "/channels" };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#A1A1AA] text-sm">Yukleniyor...</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Dashboard</h1>
        <Button
          onClick={startImportAll}
          disabled={importing}
          className="bg-[#24A1DE] hover:bg-[#1A8BC7] text-white gap-2"
          data-testid="import-all-btn"
        >
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {importing ? "Ice Aktariliyor..." : "Tumunu Ice Aktar"}
        </Button>
      </div>

      {/* Import Progress Banner */}
      <ImportProgressBanner
        progress={importProgress}
        onDismiss={() => setImportProgress(null)}
      />

      {/* Setup Wizard */}
      {setup && !setup.is_ready && (
        <Card className="bg-[#121212] border-[#24A1DE]/30 mb-6" data-testid="setup-wizard">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#24A1DE]/10 flex items-center justify-center">
                  <Radio className="w-5 h-5 text-[#24A1DE]" />
                </div>
                <div>
                  <CardTitle className="text-white font-heading text-lg">Kurulum Rehberi</CardTitle>
                  <p className="text-sm text-[#A1A1AA] mt-0.5">Telegram kanalinizi baglamak icin adimlari tamamlayin</p>
                </div>
              </div>
              <Badge variant="outline" className="border-[#24A1DE] text-[#24A1DE]">{setup.completed}/{setup.total}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <Progress value={(setup.completed / setup.total) * 100} className="h-1.5 mb-4 bg-[#27272A]" />
            <div className="space-y-2">
              {setup.steps.map((step, i) => (
                <div
                  key={step.key}
                  className={`flex items-center justify-between p-3 rounded-md border transition-colors cursor-pointer ${
                    step.done ? "bg-[#34C759]/5 border-[#34C759]/20" : i === setup.steps.findIndex(s => !s.done) ? "bg-[#24A1DE]/5 border-[#24A1DE]/30 hover:bg-[#24A1DE]/10" : "bg-[#0A0A0A] border-[#27272A] opacity-60"
                  }`}
                  onClick={() => !step.done && navigate(stepRoutes[step.key])}
                  data-testid={`setup-step-${step.key}`}
                >
                  <div className="flex items-center gap-3">
                    {step.done ? <CheckCircle className="w-5 h-5 text-[#34C759] shrink-0" /> : <Circle className="w-5 h-5 text-[#A1A1AA] shrink-0" />}
                    <div>
                      <p className={`text-sm font-medium ${step.done ? "text-[#34C759]" : "text-white"}`}>{step.title}</p>
                      <p className="text-xs text-[#A1A1AA]">{step.desc}</p>
                    </div>
                  </div>
                  {!step.done && <ArrowRight className="w-4 h-4 text-[#24A1DE] shrink-0" />}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Toplam Uye (Tum Kanallar)" value={stats?.total_all_members || 0} icon={TrendingUp} color="#24A1DE" testId="stat-total-members" />
        <StatCard title="DB Kayitli Kullanicilar" value={stats?.total_users || 0} icon={Database} color="#34C759" testId="stat-users" />
        <StatCard title="Telegram ID Olan" value={stats?.users_with_tg_id || 0} icon={Users} color="#FF9F0A" testId="stat-tg-users" />
        <StatCard title="Yan Kanallar" value={stats?.total_channels || 0} icon={Hash} color="#AF52DE" testId="stat-channels" />
      </div>

      {/* Main Channel Info */}
      {stats?.telegram_info && (
        <Card className="bg-[#121212] border-[#27272A] mb-6" data-testid="telegram-info-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg text-white">Ana Kanal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 p-3 rounded-md bg-[#0A0A0A] border border-[#24A1DE]/20">
              <div className="w-12 h-12 rounded-lg bg-[#24A1DE]/10 flex items-center justify-center shrink-0">
                <Hash className="w-6 h-6 text-[#24A1DE]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-heading font-semibold text-base">{stats.telegram_info.title || "-"}</p>
                <p className="text-[#A1A1AA] text-xs font-mono">@{stats.telegram_info.username || "-"}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-2xl font-bold text-[#24A1DE] font-heading">{stats.telegram_member_count?.toLocaleString("tr-TR") || 0}</p>
                <p className="text-xs text-[#A1A1AA]">uye</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-lg font-bold text-[#34C759] font-heading">{(stats.main_channel_imported || 0).toLocaleString("tr-TR")}</p>
                <p className="text-xs text-[#A1A1AA]">DB kayitli</p>
              </div>
              <Badge variant="outline" className="border-[#24A1DE] text-[#24A1DE] shrink-0">{stats.telegram_info.type}</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Channels Stats */}
      {stats?.all_channels_stats?.length > 0 && (
        <Card className="bg-[#121212] border-[#27272A] mb-6" data-testid="all-channels-stats">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg text-white">Tum Kanallar Istatistigi</CardTitle>
              <Badge variant="outline" className="border-[#A1A1AA] text-[#A1A1AA]">{stats.all_channels_stats.length} kanal</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {stats.all_channels_stats.map(ch => (
                <div
                  key={ch.id}
                  className="flex items-center gap-3 p-3 rounded-md bg-[#0A0A0A] border border-[#27272A] hover:border-[#3f3f46] transition-colors"
                  data-testid={`channel-stat-${ch.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#24A1DE]/10 flex items-center justify-center shrink-0">
                    {ch.is_private ? (
                      <Lock className="w-5 h-5 text-[#FF9F0A]" />
                    ) : (
                      <Globe className="w-5 h-5 text-[#34C759]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{ch.title || ch.name}</p>
                    <p className="text-xs text-[#A1A1AA] font-mono truncate">
                      {ch.username ? `@${ch.username}` : ch.telegram_channel_id}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-lg font-bold text-white font-heading">{(ch.member_count || 0).toLocaleString("tr-TR")}</p>
                    <p className="text-xs text-[#A1A1AA]">uye</p>
                  </div>
                  <div className="text-right shrink-0 min-w-[50px]">
                    <p className={`text-sm font-bold font-heading ${ch.imported_count > 0 ? "text-[#34C759]" : "text-[#A1A1AA]"}`}>
                      {(ch.imported_count || 0).toLocaleString("tr-TR")}
                    </p>
                    <p className="text-[10px] text-[#A1A1AA]">DB</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Total Summary Bar */}
            <div className="mt-4 p-3 rounded-md bg-[#24A1DE]/5 border border-[#24A1DE]/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#24A1DE]" />
                  <span className="text-sm text-[#A1A1AA]">Toplam</span>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <span className="text-lg font-bold text-[#24A1DE] font-heading">
                      {(stats.total_all_members || 0).toLocaleString("tr-TR")}
                    </span>
                    <span className="text-xs text-[#A1A1AA] ml-1">uye</span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-[#34C759] font-heading">
                      {(stats.total_imported || 0).toLocaleString("tr-TR")}
                    </span>
                    <span className="text-xs text-[#A1A1AA] ml-1">DB kayitli</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-[#121212] border-[#27272A]" data-testid="recent-users-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg text-white">Son Eklenen Kullanicilar</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_users?.length > 0 ? (
              <div className="space-y-2">
                {stats.recent_users.map((user) => (
                  <div key={user.id} className="flex items-center justify-between py-2 border-b border-[#27272A] last:border-0">
                    <div>
                      <p className="text-white text-sm font-medium">{user.first_name || user.username || "Anonim"} {user.last_name || ""}</p>
                      <p className="text-[#A1A1AA] text-xs font-mono">@{user.username || user.telegram_id}</p>
                    </div>
                    <Badge variant="outline" className={user.status === "active" ? "border-[#34C759] text-[#34C759]" : "border-[#A1A1AA] text-[#A1A1AA]"}>
                      {user.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#A1A1AA] text-sm text-center py-6">Henuz kullanici yok</p>
            )}
          </CardContent>
        </Card>

        <Card className="bg-[#121212] border-[#27272A]" data-testid="recent-messages-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg text-white">Son Mesajlar</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_messages?.length > 0 ? (
              <div className="space-y-2">
                {stats.recent_messages.map((msg) => (
                  <div key={msg.id} className="py-2 border-b border-[#27272A] last:border-0">
                    <p className="text-white text-sm truncate">{msg.content}</p>
                    <p className="text-[#A1A1AA] text-xs mt-1">{new Date(msg.sent_at).toLocaleString("tr-TR")}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#A1A1AA] text-sm text-center py-6">Henuz mesaj yok</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
